import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import backofficeApiClient, {
  CreateInternalRecruiterModel,
  InternalRecruiterResponseModel,
  BOUsersPriceAgreementEditModel,
  FullBOUserResponseModel,
  MemberLogoInfoEditModel,
  UpdateTeammateModel,
} from 'api/backofficeApiClient';
import adminApiClient, {
  BOUsersApproveStepModel,
  BOUsersPriceAgreementModel,
  BOUserTypeFilterEnum,
  BOUserRequestStatusEnum,
  ProfileCorporatePrimaryContactsInfoModel,
  ProfileCorporateTimesheetApproversInfoModel,
  ProfileCorporateSupervisorsInfoModel,
  ProfileCorporateBillingInfoModel,
  EntityInformationRequestModel,
  BankingInformationRequestModel,
  InternationalRequestModel,
  SelfEmployedRequestModel,
  CompanyRequestModel,
  AccountDocumentTypeEnum,
  ProfileTypeEnum,
  UpdateClientStatusRequestModel,
  UpdateIsEnterpriseClientRequestModel,
  UpdateIsIcpRequestModel,
  ClientStatus,
} from 'api/adminApiClient';
import { isAnyActionOf } from 'redux/utils';
import { logout } from './authenticationSlice';
import { AgreementFileParams } from './clientSlice';

interface RecruiterState {
  allMyInternalRecruiters: InternalRecruiterResponseModel[];
  recruiterCreatedAt?: number;
  recruiterDetails?: FullBOUserResponseModel;
  memberDetailsUpdatedAtByAdmin?: number;
  /** Tracks members deactivated in this session until BE profile cache is refreshed. */
  deactivatedMemberUserIds: Record<string, true>;
}

const initialState: RecruiterState = {
  allMyInternalRecruiters: [],
  recruiterCreatedAt: undefined,
  recruiterDetails: undefined,
  memberDetailsUpdatedAtByAdmin: undefined,
  deactivatedMemberUserIds: {},
};

const getAllMyInternalRecruiters = createAsyncThunk(
  'recruiter/getAllMyInternalRecruiters',
  async () => {
    return await backofficeApiClient.getAllMyInternalRecruiters();
  },
);

const createRecruiter = createAsyncThunk(
  'recruiter/createRecruiter',
  async (data: CreateInternalRecruiterModel) => {
    return await backofficeApiClient.createNewInternalRecruiter(data);
  },
);

const getMyRecruiters = createAsyncThunk(
  'recruiter/getMyRecruiters',
  async (pageNumber: number) => {
    return await backofficeApiClient.getInternalRecruiters(pageNumber);
  },
);

interface AdminRecruitersModel {
  requestId: number;
  pageNumber: number;
  searchQuery: string;
  entityFilter: BOUserTypeFilterEnum;
  requestType: BOUserRequestStatusEnum;
  profileTypes: ProfileTypeEnum[];
}

const getAllRecruiters = createAsyncThunk(
  'recruiter/getAllRecruiters',
  async ({
    pageNumber,
    searchQuery,
    requestType,
    entityFilter,
    profileTypes,
  }: AdminRecruitersModel) => {
    return await adminApiClient.getBOUsersPagedList(
      pageNumber,
      searchQuery,
      requestType,
      entityFilter,
      profileTypes,
    );
  },
);

const getRecruiterEntityInformation = createAsyncThunk(
  'recruiter/getRecruiterEntityInformation',
  async (companyId?: string) => {
    return adminApiClient.getEntityInformation(companyId);
  },
);

const updateRecruiterEntityInformation = createAsyncThunk(
  'recruiter/updateRecruiterEntityInformation',
  async (entityInfo: EntityInformationRequestModel) => {
    return adminApiClient.createOrUpdateEntityInformation(entityInfo);
  },
);

const getRecruiterBankingInformation = createAsyncThunk(
  'recruiter/getRecruiterBankingInformation',
  async (companyId?: string) => {
    return adminApiClient.getBankingInformation(companyId);
  },
);

interface SaveBankingInfoOptions {
  companyId?: string;
  bankingInfo: BankingInformationRequestModel;
}
const saveRecruiterBankingInformation = createAsyncThunk(
  'recruiter/saveRecruiterBankingInformation',
  async (options: SaveBankingInfoOptions) => {
    const { companyId, bankingInfo } = options;

    return adminApiClient.createOrUpdateBankingInformation({
      companyId,
      bankingInformation: bankingInfo,
    });
  },
);

const getCorporateTABillingInformation = createAsyncThunk(
  'recruiter/getCorporateTABillingInformation',
  async (companyId: string | undefined) => {
    return adminApiClient.getCorporateBillingInfo(companyId);
  },
);

interface UpdateBillingInfoOptions {
  companyId?: string;
  billingInfo: ProfileCorporateBillingInfoModel;
}
const updateCorporateTABillingInformation = createAsyncThunk(
  'recruiter/updateCorporateTABillingInformation',
  async ({ companyId, billingInfo }: UpdateBillingInfoOptions) => {
    return adminApiClient.setCorporateBillingInfo({ companyId, billingInfo });
  },
);

const getCorporateTASupervisorInformation = createAsyncThunk(
  'recruiter/getCorporateTASupervisorInformation',
  async (companyId: string | undefined) => {
    return adminApiClient.getCorporateSupervisors(companyId);
  },
);

interface UpdateSupervisorInfoOptions {
  companyId?: string;
  supervisorsData: ProfileCorporateSupervisorsInfoModel;
}
const updateCorporateTASupervisorInformation = createAsyncThunk(
  'recruiter/updateCorporateTASupervisorInformation',
  async ({ companyId, supervisorsData }: UpdateSupervisorInfoOptions) => {
    return adminApiClient.setCorporateSupervisorsInfo({
      companyId,
      supervisorsData,
    });
  },
);

const getCorporateTATimesheetApproverInformation = createAsyncThunk(
  'recruiter/getCorporateTATimesheetApproverInformation',
  async (companyId: string | undefined) => {
    return adminApiClient.getCorporateTimesheetApprovers(companyId);
  },
);

interface UpdateTimesheetApproverInfoOptions {
  companyId?: string;
  timesheetApproversData: ProfileCorporateTimesheetApproversInfoModel;
}
const updateCorporateTATimesheetApproverInformation = createAsyncThunk(
  'recruiter/updateCorporateTATimesheetApproverInformation',
  async ({
    companyId,
    timesheetApproversData,
  }: UpdateTimesheetApproverInfoOptions) => {
    return adminApiClient.setCorporateTimesheetApproversInfo({
      companyId,
      timesheetApproversData,
    });
  },
);

const getCorporateTAPrimaryContactInfo = createAsyncThunk(
  'recruiter/getPrimaryContactInfo',
  async (companyId: string | undefined) => {
    return adminApiClient.getCorporatePrimaryContacts(companyId);
  },
);

interface UpdatePrimaryContactInfoOptions {
  companyId?: string;
  primaryContactsData: ProfileCorporatePrimaryContactsInfoModel;
}
const updateCorporateTAPrimaryContactInfo = createAsyncThunk(
  'recruiter/updatePrimaryContactInfo',
  async ({
    companyId,
    primaryContactsData,
  }: UpdatePrimaryContactInfoOptions) => {
    return adminApiClient.setCorporatePrimaryContactsInfo({
      companyId,
      primaryContactsData,
    });
  },
);

const saveRecruiterCompanyGeneralInfo = createAsyncThunk(
  'recruiter/saveRecruiterCompanyGeneralInfo',
  async (profile: CompanyRequestModel) => {
    return adminApiClient.updateCompanyProfile(profile);
  },
);

const saveRecruiterSelfEmployedGeneralInfo = createAsyncThunk(
  'recruiter/saveRecruiterSelfEmployedGeneralInfo',
  async (profile: SelfEmployedRequestModel) => {
    return adminApiClient.updateSelfEmployeeProfile(profile);
  },
);

const saveRecruiterInternationalGeneralInfo = createAsyncThunk(
  'recruiter/saveRecruiterInternationalGeneralInfo',
  async (profile: InternationalRequestModel) => {
    return adminApiClient.updateInternationalProfile(profile);
  },
);

const checkInvitedUser = createAsyncThunk(
  'recruiter/checkInvitedUser',
  async (email: string) => {
    return backofficeApiClient.getInvitedUserDetail(email);
  },
);

const sendInviteUser = createAsyncThunk(
  'recruiter/sendInviteUser',
  async (email: string) => {
    return backofficeApiClient.inviteUser({ email });
  },
);

const getRecruiterDetailsByKey = createAsyncThunk(
  'recruiter/getRecruiterDetailsByKey',
  async (key: string) => {
    return await adminApiClient.getBOAccountByUserKey(key);
  },
);

const getRecruiterDetailsById = createAsyncThunk(
  'recruiter/getRecruiterDetailsById',
  async (id: string) => {
    return adminApiClient.getBOAccountByUserId(id);
  },
);

const getRecruiterBankingInfoFile = createAsyncThunk(
  'recruiter/getRecruiterBankingInfoFile',
  async (userId: string | undefined) => {
    return await adminApiClient.getBankingInfoFile(userId);
  },
);

const getRecruiterEntityInfoFile = createAsyncThunk(
  'recruiter/getRecruiterEntityInfoFile',
  async (userId: string | undefined) => {
    return await adminApiClient.getEntityInfoFile(userId);
  },
);

const getMemberPriceAgreement = createAsyncThunk(
  'recruiter/geMemberPriceAgreement',
  async (companyId: string) => {
    return adminApiClient.getMemberPriceAgreement(companyId as string);
  },
);

export interface RecruiterPriceAgreementFileParams extends AgreementFileParams {
  userId?: string;
}

const getRecruiterPriceAgreementInfoFile = createAsyncThunk(
  'recruiter/getRecruiterPriceAgreementInfoFile',
  async ({
    userId,
    fileKey,
    fileName,
    isAdminRole,
  }: RecruiterPriceAgreementFileParams) => {
    return isAdminRole
      ? adminApiClient.getPriceAgreementFile(
          userId as string,
          fileKey,
          fileName,
        )
      : backofficeApiClient.getPriceAgreementFile(fileKey, fileName);
  },
);

const completeRecruiterProgressStep = createAsyncThunk(
  'recruiter/completeRecruiterProgressStep',
  async (stepData: BOUsersApproveStepModel) => {
    return adminApiClient.approveStep(stepData);
  },
);

const createRecruiterPriceAgreement = createAsyncThunk(
  'recruiter/createRecruiterPriceAgreement',
  async (priceAgreement: BOUsersPriceAgreementModel) => {
    return await adminApiClient.createBOUsersPriceAgreement(priceAgreement);
  },
);

const editPriceAgreement = createAsyncThunk(
  'recruiter/editPriceAgreement',
  async (priceAgreement: BOUsersPriceAgreementModel) => {
    return await adminApiClient.createBOUsersPriceAgreement(priceAgreement);
  },
);

const changeRecruiterPriceAgreement = createAsyncThunk(
  'recruiter/changeRecruiterPriceAgreement',
  async (priceAgreement: BOUsersPriceAgreementEditModel) => {
    return await backofficeApiClient.editPriceAgreement(priceAgreement);
  },
);

interface CreateCommentForRecruiterModel {
  entityId: string;
  text: string;
  commentId?: string;
}

const createCommentForRecruiter = createAsyncThunk(
  'recruiter/createCommentForRecruiter',
  async ({ entityId, text }: CreateCommentForRecruiterModel) => {
    return adminApiClient.createNewMemberComment({ memberId: entityId, text });
  },
);

const updateCommentForRecruiter = createAsyncThunk(
  'recruiter/updateCommentForRecruiter',
  async ({ commentId, text }: CreateCommentForRecruiterModel) => {
    return adminApiClient.updateMemberComment({ id: commentId, text });
  },
);

interface CommentsForRecruiterModel {
  entityId: string;
  pageNumber: number;
  requestId: number;
}

const getCommentsForRecruiter = createAsyncThunk(
  'recruiter/getCommentsForRecruiter',
  async ({ entityId, pageNumber }: CommentsForRecruiterModel) => {
    return adminApiClient.getMemberComments(pageNumber, entityId);
  },
);

interface HistoryForRecruiterModel {
  entityId: string;
  pageNumber: number;
}
const getHistoryDataForRecruiter = createAsyncThunk(
  'recruiter/getHistoryDataForRecruiter',
  async ({ entityId, pageNumber }: HistoryForRecruiterModel) => {
    return adminApiClient.getMemberHistory(entityId, 20, pageNumber);
  },
);

interface HistoryFileRequestModel {
  userId?: string;
  fileKey?: string;
  documentType?: AccountDocumentTypeEnum;
}
const getRecruiterHistoryFile = createAsyncThunk(
  'recruiter/getRecruiterHistoryFile',
  async ({ userId, fileKey, documentType }: HistoryFileRequestModel) => {
    return adminApiClient.getHistoryFileByKey(userId, fileKey, documentType);
  },
);

interface MemberLogoInfoModel {
  isAdminRole: boolean;
  companyId?: string;
  body: MemberLogoInfoEditModel;
}

const setMemberLogoInfo = createAsyncThunk(
  'recruiter/setMemberLogoInfo',
  async ({ isAdminRole, body, companyId }: MemberLogoInfoModel) => {
    return isAdminRole
      ? adminApiClient.setMemberLogoInfo(companyId as string, body)
      : backofficeApiClient.setMemberLogoInfo(body);
  },
);

const getMemberLogoInfo = createAsyncThunk(
  'recruiter/getMemberLogoInfo',
  async (companyId: string) => {
    return await adminApiClient.getMemberLogoInfo(companyId);
  },
);

interface UpdateCompanyClientStatusOptions {
  companyId: string;
  status: ClientStatus;
}

const updateCompanyClientStatus = createAsyncThunk(
  'recruiter/updateCompanyClientStatus',
  async ({ companyId, status }: UpdateCompanyClientStatusOptions) => {
    const requestBody: UpdateClientStatusRequestModel = {
      status,
    };
    return await adminApiClient.updateCompanyClientStatus(
      companyId,
      requestBody,
    );
  },
);

interface UpdateCompanyIsEnterpriseClientOptions {
  companyId: string;
  isEnterpriseClient: boolean;
}

const updateCompanyIsEnterpriseClient = createAsyncThunk(
  'recruiter/updateCompanyIsEnterpriseClient',
  async ({
    companyId,
    isEnterpriseClient,
  }: UpdateCompanyIsEnterpriseClientOptions) => {
    const requestBody: UpdateIsEnterpriseClientRequestModel = {
      isEnterpriseClient,
    };
    return await adminApiClient.updateCompanyIsEnterpriseClient(
      companyId,
      requestBody,
    );
  },
);

interface UpdateCompanyIsIcpOptions {
  companyId: string;
  isIcp: boolean;
}

const updateCompanyIsIcp = createAsyncThunk(
  'recruiter/updateCompanyIsIcp',
  async ({ companyId, isIcp }: UpdateCompanyIsIcpOptions) => {
    const requestBody: UpdateIsIcpRequestModel = { isIcp };
    return await adminApiClient.updateCompanyIsIcp(companyId, requestBody);
  },
);

const getMyTeammates = createAsyncThunk(
  'recruiter/getMyTeammates',
  async (pageNumber: number) => {
    return backofficeApiClient.getTeammates(pageNumber);
  },
);

const deleteRegisteredTeammate = createAsyncThunk(
  'recruiter/deleteRegisteredTeammate',
  async (userId: string) => {
    return await backofficeApiClient.deleteRegisteredTeammate(userId);
  },
);

const deleteUnregisteredTeammate = createAsyncThunk(
  'recruiter/deleteUnregisteredTeammate',
  async (email: string) => {
    return await backofficeApiClient.deleteUnregisteredTeammate(email);
  },
);

const editTeammate = createAsyncThunk(
  'recruiter/editTeammate',
  async (teammate: UpdateTeammateModel) => {
    return backofficeApiClient.editTeammate(teammate);
  },
);

const teammateDeactivation = createAsyncThunk(
  'recruiter/teammateDeactivation',
  async (teammateId: string) => {
    return backofficeApiClient.setTeammateIsLockedOut(teammateId, true);
  },
);

const teammateActivation = createAsyncThunk(
  'recruiter/teammateActivation',
  async (teammateId: string) => {
    return backofficeApiClient.setTeammateIsLockedOut(teammateId, false);
  },
);

interface MemberListExcelRequestModel {
  memberName: string;
  requestType: BOUserRequestStatusEnum;
  entityFilter: BOUserTypeFilterEnum;
  profileTypes: ProfileTypeEnum[];
}
const getMemberListExcel = createAsyncThunk(
  'recruiter/getMemberListExcel',
  async ({
    memberName,
    requestType,
    entityFilter,
    profileTypes,
  }: MemberListExcelRequestModel) => {
    const result = await adminApiClient.getBOUsersExcel(
      undefined,
      memberName,
      requestType,
      entityFilter,
      profileTypes,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const recruiterSlice = createSlice({
  name: 'recruiter',
  initialState,
  reducers: {
    markMemberDeactivated: (state, action: PayloadAction<string>) => {
      state.deactivatedMemberUserIds[action.payload] = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addCase(getAllMyInternalRecruiters.fulfilled, (state, action) => {
      state.allMyInternalRecruiters = action.payload;
    });
    builder.addCase(createRecruiter.fulfilled, (state) => {
      state.recruiterCreatedAt = Date.now();
    });
    builder.addCase(getRecruiterDetailsByKey.pending, (state) => {
      state.recruiterDetails = undefined;
    });
    builder.addCase(getRecruiterDetailsByKey.fulfilled, (state, action) => {
      const payload = action.payload;

      if (payload?.userId && state.deactivatedMemberUserIds[payload.userId]) {
        state.recruiterDetails = { ...payload, isLockedOut: true };
        return;
      }

      state.recruiterDetails = payload;
    });
    builder.addCase(getRecruiterDetailsByKey.rejected, (state) => {
      state.recruiterDetails = undefined;
    });
    builder.addMatcher(
      isAnyActionOf(
        // Reload history section on recruiter's sections updates
        // Updating Member & Corprorate TA sections by admin
        saveRecruiterCompanyGeneralInfo.fulfilled,
        saveRecruiterInternationalGeneralInfo.fulfilled,
        saveRecruiterSelfEmployedGeneralInfo.fulfilled,
        editPriceAgreement.fulfilled,
        completeRecruiterProgressStep.fulfilled,
        saveRecruiterBankingInformation.fulfilled,
        updateRecruiterEntityInformation.fulfilled,
        updateCompanyClientStatus.fulfilled,
        // Updating Corprorate TA sections by admin
        updateCorporateTABillingInformation.fulfilled,
        updateCorporateTAPrimaryContactInfo.fulfilled,
        updateCorporateTASupervisorInformation.fulfilled,
        updateCorporateTATimesheetApproverInformation.fulfilled,
      ),
      (state) => {
        state.memberDetailsUpdatedAtByAdmin = Date.now();
      },
    );
  },
});

const { reducer, actions } = recruiterSlice;

const { markMemberDeactivated } = actions;

export {
  markMemberDeactivated,
  createRecruiter,
  getMyRecruiters,
  getAllRecruiters,
  getMemberPriceAgreement,
  getRecruiterDetailsById,
  getRecruiterDetailsByKey,
  getAllMyInternalRecruiters,
  completeRecruiterProgressStep,
  createRecruiterPriceAgreement,
  editPriceAgreement,
  changeRecruiterPriceAgreement,
  getRecruiterEntityInfoFile,
  createCommentForRecruiter,
  getCommentsForRecruiter,
  setMemberLogoInfo,
  getHistoryDataForRecruiter,
  getRecruiterPriceAgreementInfoFile,
  sendInviteUser,
  getMemberLogoInfo,
  getRecruiterBankingInfoFile,
  deleteRegisteredTeammate,
  deleteUnregisteredTeammate,
  editTeammate,
  getMyTeammates,
  checkInvitedUser,
  updateRecruiterEntityInformation,
  getCorporateTABillingInformation,
  updateCorporateTABillingInformation,
  getCorporateTASupervisorInformation,
  updateCorporateTASupervisorInformation,
  getCorporateTATimesheetApproverInformation,
  updateCorporateTATimesheetApproverInformation,
  getCorporateTAPrimaryContactInfo,
  updateCorporateTAPrimaryContactInfo,
  saveRecruiterBankingInformation,
  getRecruiterEntityInformation,
  getRecruiterBankingInformation,
  saveRecruiterCompanyGeneralInfo,
  saveRecruiterInternationalGeneralInfo,
  saveRecruiterSelfEmployedGeneralInfo,
  getRecruiterHistoryFile,
  getMemberListExcel,
  teammateActivation,
  teammateDeactivation,
  updateCommentForRecruiter,
  updateCompanyClientStatus,
  updateCompanyIsEnterpriseClient,
  updateCompanyIsIcp,
};
export default reducer;
